import { DeleteOutlined, LoadingOutlined, PaperClipOutlined, UploadOutlined } from "@ant-design/icons";
import { uploadMedia } from "@api/uploadMediaApi";
import { Button, message, Upload } from "antd";
import { RcFile } from "antd/lib/upload";
import React, { FC, useState } from "react";
import styled from "styled-components";

const FileList = styled.div`
  margin-top: 8px;
`;

const FileItem = styled.div`
  display: flex;
  align-items: center;
  gap: 8px;
  padding: 4px 0;
  font-size: 13px;
  color: ${({ theme }) => theme.colorNeutral700};
`;

const FileName = styled.a`
  flex: 1;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

export interface UploadedFile {
  /** 已存在檔案才有id */
  id?: number;
  /** 上傳後取得的 S3 暫存路徑 */
  filePath: string;
  /** 顯示用的檔案名稱 */
  fileName: string;
}

interface Props {
  dirPrefix: string; /** 後端提供 dir prefix，例如 'vendor/contract' */
  value?: UploadedFile[]; /** 已上傳的檔案列表（顯示用） */
  onChange?: (files: UploadedFile[]) => void; /** 檔案變更時，回傳最新的完整列表 */
  readOnly?: boolean; /** 是否唯讀 */
  hint?: string; /** 說明文字，顯示在上傳按鈕下方 */
  showHint?: boolean;
}

const MultipleFileUpload: FC<Props> = ({
  dirPrefix,
  value = [],
  onChange,
  readOnly = false,
  hint,
  showHint = true,
}) => {
  const [uploading, setUploading] = useState(false);
  const handleUpload = async (file: RcFile): Promise<void> => {
    setUploading(true);
    try {
      const filePath = await uploadMedia(file, dirPrefix);
      const newFile: UploadedFile = { filePath, fileName: file.name };
      onChange?.([...value, newFile]);
    } catch {
      message.error(`「${file.name}」上傳失敗`);
    } finally {
      setUploading(false);
    }
  };

  const handleRemove = (index: number) => {
    const updated = value.filter((_, i) => i !== index);
    onChange?.(updated);
  };

  return (
    <div>
      {!readOnly && (
        <Upload
          multiple
          showUploadList={false}
          customRequest={({ file, onSuccess }) => {
            handleUpload(file as RcFile).then(() => onSuccess?.("ok"));
          }}
          disabled={uploading}
        >
          <Button icon={uploading ? <LoadingOutlined /> : <UploadOutlined />} disabled={uploading}>
            上傳檔案
          </Button>
        </Upload>
      )}
      {hint && showHint && <div style={{ marginTop: 4, fontSize: 12, color: "#888" }}>{hint}</div>}
      <FileList>
        {value.map((file, index) => (
          <FileItem key={file.id ?? file.filePath}>
            <PaperClipOutlined />
            <FileName href={file.filePath} target="_blank" rel="noreferrer">
              {file.fileName}
            </FileName>
            {!readOnly && (
              <DeleteOutlined style={{ cursor: "pointer", color: "#ff4d4f" }} onClick={() => handleRemove(index)} />
            )}
          </FileItem>
        ))}
      </FileList>
    </div>
  );
};

export default MultipleFileUpload;
